import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {FancyTextList} from '@Components/fancy-text-list';
import {getFancyTextFontFamilyFromId, getIdFromFancyTextFontFamily} from '@Libraries/fancy-text-library';
import {FancyTextType} from '@Components/fancy-text-list/fancy-text-list.types';
import {ContextualMenuDropdownOption} from '@Components/contextual-menu/components/contextual-menu-dropdown-option';
import {DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {useGetFancyTextFonts} from '@Hooks/poster-editor/useGetFancyTextFonts';
import styles from './fancy-text-font-option.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

export function FancyTextFontOption(): ReactElement<any> {
  const [isFontBeingApplied, setIsFontBeingApplied] = useState(false);
  const {isLoading, fonts} = useGetFancyTextFonts();
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemFontFamily = useGetKeyFromFancyTextItem('fontFamily');

  if (!activeItem || itemFontFamily === undefined) {
    throw new Error('Active item is null');
  }

  if (!activeItem.isFancyText()) {
    throw new Error(`Expected active item type to be fancy text, instead got ${activeItem.gitype}`);
  }

  const getCustomPopUp = (): ReactElement<any> | undefined => {
    if (!fonts) {
      return undefined;
    }
    return (
      <FancyTextList
        className={`${styles.list} spacing-m-0 `}
        data={{
          type: FancyTextType.FONT,
          fancyTextFonts: fonts,
        }}
        onClick={(id: string): void => {
          const fontFamily = getFancyTextFontFamilyFromId(id);
          if (!fontFamily) {
            return;
          }
          if (itemFontFamily === fontFamily) {
            return;
          }
          setIsFontBeingApplied(true);
          void activeItem.updateFancyTextFont(fontFamily).finally((): void => {
            setIsFontBeingApplied(false);
          });
        }}
        selectedId={getIdFromFancyTextFontFamily(itemFontFamily)}
        doScrollIntoView
      />
    );
  };

  return (
    <ContextualMenuDropdownOption
      customPopup={getCustomPopUp()}
      id="fancy-text-font-option"
      label={window.i18next.t('pmwjs_font')}
      dropdownSelectorText={itemFontFamily}
      isLoading={isLoading || isFontBeingApplied}
      isDisabled={isFontBeingApplied}
      position={DROPDOWN_POSITION.FIXED}
    />
  );
}
